import type {
  AcidBasePhSpec,
  AcidBaseProblem,
  BronstedSpecies,
  ConjugateCase,
  WeakSpecies,
} from './acidBaseCalculationsData';
import { WEAK_SPECIES } from './acidBaseCalculationsData';

export type AcidBaseMode = 'theory' | 'ph' | 'weak';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const ACID_BASE_TOOL_IDENTITY = {
  id: 'acid-base-calculations',
  toolKey: 'chemistry-acid-base-calculations',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const ACID_BASE_MODES: readonly AcidBaseMode[] = ['theory', 'ph', 'weak'];

/**
 * Default mode by learner Year (study-chemistry-tools-prd.md §8.1 CT-MODE). Year 11 starts with
 * Brønsted–Lowry theory; Year 12 starts with weak acid and base calculations. Strong-solution pH
 * is always one click away and is never a Year default.
 */
export const ACID_BASE_MODE_YEARS: readonly { mode: AcidBaseMode; fromYear: number }[] = [
  { mode: 'theory', fromYear: 11 },
  { mode: 'weak', fromYear: 12 },
];

export const ACID_BASE_REVIEW_STATUS = 'pending-teaching-review' as const;
export const ACID_BASE_CONDITIONS =
  'All solutions are aqueous at 25 °C, where Kw = [H₃O⁺][OH⁻] = 1.0 × 10⁻¹⁴ and pH + pOH = 14.00.';

export interface AcidBaseSource {
  label: string;
  detail: string;
}

export const ACID_BASE_SOURCES: AcidBaseSource[] = [
  {
    label: 'IUPAC Gold Book',
    detail: 'Brønsted acid and base, conjugate acid–base pair, pH, ionic product of water.',
  },
  {
    label: 'CRC Handbook of Chemistry and Physics',
    detail:
      'Kw = 1.0 × 10⁻¹⁴ at 25 °C; dissociation constants of ethanoic, methanoic, lactic, benzoic, hydrofluoric and hydrocyanic acids and ammonia at 25 °C.',
  },
  {
    label: 'Standard senior chemistry texts',
    detail:
      'Weak-acid approximation [HA] ≈ c when ionisation is below 5 %; Kb of methylamine 4.4 × 10⁻⁴.',
  },
];

export const KW = 1.0e-14;
export const PKW = 14;
export const NEUTRAL_PH = 7;
export const ML_PER_L = 1000;
/** The weak-acid approximation is accepted when no more than 5 % of the acid ionises. */
export const APPROXIMATION_LIMIT_PERCENT = 5;
/** Below this, water's own ionisation matters and the simple strong-acid model fails closed. */
export const MIN_STRONG_CONCENTRATION = 1e-6;
/** pH, pOH and pKa answers are marked to ±0.02 (two decimal places). */
export const LOG_ANSWER_TOLERANCE = 0.02;
/** Concentrations, constants and percentages are marked to ±2 %. */
export const RELATIVE_ANSWER_TOLERANCE = 0.02;
const PERCENT = 100;
const FLOAT_EPSILON = 1e-12;

export const ACID_BASE_MODEL_BOUNDARIES = [
  'Every calculation assumes dilute aqueous solutions at 25 °C. Kw, Ka and Kb change with temperature.',
  'Concentrations stand in for activities, so very concentrated solutions behave less ideally than the model.',
  'Strong acids and bases are treated as fully ionised. Below 1 × 10⁻⁶ mol L⁻¹ the model stops, because water’s own ions matter.',
  'Weak acids and bases use one ionisation step. Polyprotic second steps are not modelled.',
  'This is a calculation model, not a measured pH. Real pH meters need calibration and temperature correction.',
];

// ---------------------------------------------------------------------------------------------
// Number handling
// ---------------------------------------------------------------------------------------------

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '-': '⁻',
};

const PLAIN_DIGITS: Record<string, string> = Object.fromEntries(
  Object.entries(SUPERSCRIPT_DIGITS).map(([plain, superscript]) => [superscript, plain]),
);

const SCIENTIFIC_LOWER = 1e-3;
const SCIENTIFIC_UPPER = 1e4;

/** Formats with significant figures, switching to a × 10ⁿ notation for very small or large values. */
export function formatSci(value: number, significantFigures = 3): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const magnitude = Math.abs(value);
  if (magnitude >= SCIENTIFIC_LOWER && magnitude < SCIENTIFIC_UPPER) {
    return String(Number(value.toPrecision(significantFigures)));
  }
  const [mantissa, exponent] = value.toExponential(significantFigures - 1).split('e');
  const power = String(Number(exponent))
    .split('')
    .map((character) => SUPERSCRIPT_DIGITS[character] ?? character)
    .join('');
  return `${mantissa} × 10${power}`;
}

export function formatLog(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : '—';
}

/**
 * Reads a learner's number. Accepts 0.0013, 1.3e-3, 1.3 x 10^-3, 1.3×10⁻³ and a Unicode minus.
 * Anything else returns NaN so the checker can fail closed.
 */
export function parseNumericAnswer(raw: string): number {
  const text = raw
    .trim()
    .replace(/−/g, '-')
    .replace(/\s+/g, '')
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻]/g, (character) => PLAIN_DIGITS[character] ?? character);
  if (!text) return Number.NaN;
  const scientific = text.match(/^([+-]?(?:\d+\.?\d*|\.\d+))[x×*]10\^?([+-]?\d+)$/i);
  if (scientific) return Number(scientific[1]) * 10 ** Number(scientific[2]);
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) return Number.NaN;
  return Number(text);
}

export const log10 = (value: number): number => Math.log10(value);
export const pFrom = (value: number): number => -Math.log10(value);

// ---------------------------------------------------------------------------------------------
// Theory: Brønsted–Lowry roles
// ---------------------------------------------------------------------------------------------

export type ProtonRole = 'acid' | 'base' | 'conjugate-acid' | 'conjugate-base';

export const PROTON_ROLES: readonly ProtonRole[] = [
  'acid',
  'base',
  'conjugate-acid',
  'conjugate-base',
];

export const ROLE_LABELS: Record<ProtonRole, string> = {
  acid: 'Acid (H⁺ donor)',
  base: 'Base (H⁺ acceptor)',
  'conjugate-acid': 'Conjugate acid',
  'conjugate-base': 'Conjugate base',
};

export type AnswerStatus = 'correct' | 'misconception' | 'incorrect' | 'invalid';

export interface AcidBaseCheck {
  status: AnswerStatus;
  title: string;
  feedback: string;
}

export interface RoleCheck extends AcidBaseCheck {
  /** Per-position correctness, in the order the species appear in the equation. */
  positions: boolean[];
}

export interface ConjugatePairs {
  acid: string;
  base: string;
  conjugateAcid: string;
  conjugateBase: string;
}

/** Reads the four species by role. Fails closed when a role is missing or repeated. */
export function conjugatePairs(item: ConjugateCase): ConjugatePairs | null {
  const byRole = new Map<ProtonRole, string>();
  for (const species of item.species) {
    if (byRole.has(species.role)) return null;
    byRole.set(species.role, species.formula);
  }
  const acid = byRole.get('acid');
  const base = byRole.get('base');
  const conjugateAcid = byRole.get('conjugate-acid');
  const conjugateBase = byRole.get('conjugate-base');
  if (!acid || !base || !conjugateAcid || !conjugateBase) return null;
  return { acid, base, conjugateAcid, conjugateBase };
}

/**
 * Checks the chemistry of a reviewed case: each conjugate pair differs by exactly one H⁺, the
 * acid and base are reactants, and hydrogen and charge are conserved across the arrow.
 */
export function validateConjugateCase(item: ConjugateCase): string[] {
  const errors: string[] = [];
  if (item.species.length !== 4) return [`${item.id}: needs exactly four species`];
  const find = (role: ProtonRole) => item.species.find((species) => species.role === role);
  const acid = find('acid');
  const base = find('base');
  const conjugateAcid = find('conjugate-acid');
  const conjugateBase = find('conjugate-base');
  if (!acid || !base || !conjugateAcid || !conjugateBase || !conjugatePairs(item)) {
    return [`${item.id}: each role must appear once`];
  }
  const isPair = (
    donor: { core: string; hydrogens: number; charge: number },
    acceptor: { core: string; hydrogens: number; charge: number },
  ) =>
    donor.core === acceptor.core &&
    donor.hydrogens === acceptor.hydrogens + 1 &&
    donor.charge === acceptor.charge + 1;
  if (!isPair(acid, conjugateBase)) errors.push(`${item.id}: acid/conjugate base pair is wrong`);
  if (!isPair(conjugateAcid, base)) errors.push(`${item.id}: conjugate acid/base pair is wrong`);
  const reactants = item.species.slice(0, 2).map((species) => species.role);
  if (!reactants.includes('acid') || !reactants.includes('base')) {
    errors.push(`${item.id}: acid and base must be the reactants`);
  }
  const sum = (list: readonly BronstedSpecies[], key: 'hydrogens' | 'charge') =>
    list.reduce((total, species) => total + species[key], 0);
  const left = item.species.slice(0, 2);
  const right = item.species.slice(2);
  if (sum(left, 'hydrogens') !== sum(right, 'hydrogens'))
    errors.push(`${item.id}: H not conserved`);
  if (sum(left, 'charge') !== sum(right, 'charge')) errors.push(`${item.id}: charge not conserved`);
  return errors;
}

export function checkConjugateRoles(
  item: ConjugateCase,
  answer: readonly (ProtonRole | '')[],
): RoleCheck {
  const pairs = conjugatePairs(item);
  if (!pairs || answer.length !== item.species.length) {
    return {
      status: 'invalid',
      title: 'Case unavailable',
      feedback: 'This case cannot be checked.',
      positions: [],
    };
  }
  if (answer.some((role) => role === '')) {
    return {
      status: 'invalid',
      title: 'Choose every role',
      feedback: 'Give each of the four species a role before you check.',
      positions: answer.map(() => false),
    };
  }
  const positions = item.species.map((species, index) => species.role === answer[index]);
  const pairText = `Conjugate pairs differ by exactly one H⁺: ${pairs.acid} / ${pairs.conjugateBase} and ${pairs.conjugateAcid} / ${pairs.base}.`;
  if (positions.every(Boolean)) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `${pairs.acid} donates H⁺ to ${pairs.base}. ${pairText} ${item.note}`,
      positions,
    };
  }
  const reactantRoles = answer.slice(0, 2);
  const productRoles = answer.slice(2);
  const conjugateOnLeft = reactantRoles.some((role) => role.startsWith('conjugate'));
  const plainOnRight = productRoles.some((role) => role === 'acid' || role === 'base');
  if (conjugateOnLeft || plainOnRight) {
    return {
      status: 'misconception',
      title: 'Not yet — check which side is which',
      feedback: `In this convention the acid and base are the reactants. The conjugate acid and conjugate base are the products they turn into. ${pairText}`,
      positions,
    };
  }
  if (!positions[0] || !positions[1]) {
    return {
      status: 'misconception',
      title: 'Not yet — follow the H⁺',
      feedback: `Find the reactant that loses an H⁺. ${pairs.acid} becomes ${pairs.conjugateBase}, so ${pairs.acid} is the proton donor (acid). ${pairs.base} gains that H⁺ and becomes ${pairs.conjugateAcid}.`,
      positions,
    };
  }
  return {
    status: 'misconception',
    title: 'Not yet — match each product to its partner',
    feedback: `${pairs.conjugateBase} is what is left after ${pairs.acid} loses H⁺, so it is the conjugate base. ${pairs.conjugateAcid} forms when ${pairs.base} gains H⁺, so it is the conjugate acid.`,
    positions,
  };
}

// ---------------------------------------------------------------------------------------------
// pH, pOH and weak-acid calculations
// ---------------------------------------------------------------------------------------------

export type AcidBaseQuantity = 'pH' | 'pOH' | 'H' | 'OH' | 'Ka' | 'Kb' | 'pKa' | 'percent';

export const QUANTITY_LABELS: Record<AcidBaseQuantity, string> = {
  pH: 'pH',
  pOH: 'pOH',
  H: '[H₃O⁺]',
  OH: '[OH⁻]',
  Ka: 'Ka',
  Kb: 'Kb',
  pKa: 'pKa',
  percent: 'percentage ionisation',
};

export const QUANTITY_UNITS: Record<AcidBaseQuantity, string> = {
  pH: '',
  pOH: '',
  H: 'mol L⁻¹',
  OH: 'mol L⁻¹',
  Ka: '',
  Kb: '',
  pKa: '',
  percent: '%',
};

const LOG_QUANTITIES: ReadonlySet<AcidBaseQuantity> = new Set(['pH', 'pOH', 'pKa']);

export interface NumericMisconception {
  value: number;
  feedback: string;
}

export interface WeakApproximation {
  /** Percentage ionisation from the [HA] ≈ c shortcut. */
  approximatePercent: number;
  valid: boolean;
}

export interface AcidBaseSolution {
  value: number;
  quantity: AcidBaseQuantity;
  working: string[];
  misconceptions: NumericMisconception[];
  correctReason: string;
  /** Model pH of the solution when one exists, for the pH strip. */
  modelPH: number | null;
  /** Model percentage ionisation of a weak species, for the ionisation grid. */
  modelPercent: number | null;
  approximation: WeakApproximation | null;
}

export type SolveResult = { value: AcidBaseSolution; error: null } | { value: null; error: string };

const fail = (error: string): SolveResult => ({ value: null, error });

export function weakSpecies(id: string): WeakSpecies | undefined {
  return WEAK_SPECIES.find((species) => species.id === id);
}

/** Exact [H₃O⁺] (or [OH⁻]) for a weak species from x² / (c − x) = K. */
export function exactIonised(k: number, concentration: number): number {
  return (-k + Math.sqrt(k * k + 4 * k * concentration)) / 2;
}

export function approximateIonised(k: number, concentration: number): number {
  return Math.sqrt(k * concentration);
}

const isPositive = (value: number) => Number.isFinite(value) && value > 0;

function phFromHydronium(hydronium: number): number {
  return pFrom(hydronium);
}

function phFromHydroxide(hydroxide: number): number {
  return PKW - pFrom(hydroxide);
}

function solution(
  partial: Omit<AcidBaseSolution, 'modelPH' | 'modelPercent' | 'approximation'> &
    Partial<Pick<AcidBaseSolution, 'modelPH' | 'modelPercent' | 'approximation'>>,
): SolveResult {
  return {
    value: {
      modelPH: null,
      modelPercent: null,
      approximation: null,
      ...partial,
    },
    error: null,
  };
}

function solveStrong(spec: AcidBasePhSpec): SolveResult {
  switch (spec.kind) {
    case 'strong-acid': {
      if (!isPositive(spec.concentration)) return fail('Concentration must be above zero.');
      if (spec.concentration < MIN_STRONG_CONCENTRATION) {
        return fail('Below 1 × 10⁻⁶ mol L⁻¹, water’s own ionisation matters. This model stops.');
      }
      const pH = phFromHydronium(spec.concentration);
      return solution({
        value: pH,
        quantity: 'pH',
        modelPH: pH,
        working: [
          `${spec.formula} is a strong acid, so it ionises completely: [H₃O⁺] = ${formatSci(spec.concentration)} mol L⁻¹.`,
          `pH = −log₁₀[H₃O⁺] = −log₁₀(${formatSci(spec.concentration)}) = ${formatLog(pH)}`,
        ],
        misconceptions: [
          {
            value: -pH,
            feedback:
              'Check the sign. pH = −log₁₀[H₃O⁺]. The minus sign makes pH positive whenever [H₃O⁺] is below 1 mol L⁻¹.',
          },
          {
            value: PKW - pH,
            feedback: `That is the pOH of this solution. An acid solution has pH below 7, so pH = −log₁₀[H₃O⁺] directly.`,
          },
        ],
        correctReason: `A strong monoprotic acid gives one H₃O⁺ for each acid particle, so [H₃O⁺] equals the acid concentration.`,
      });
    }
    case 'strong-base': {
      if (!isPositive(spec.concentration)) return fail('Concentration must be above zero.');
      const hydroxide = spec.concentration * spec.hydroxidePerFormula;
      if (hydroxide < MIN_STRONG_CONCENTRATION) {
        return fail('Below 1 × 10⁻⁶ mol L⁻¹, water’s own ionisation matters. This model stops.');
      }
      const pOH = pFrom(hydroxide);
      const pH = PKW - pOH;
      const singleHydroxidePH = phFromHydroxide(spec.concentration);
      return solution({
        value: pH,
        quantity: 'pH',
        modelPH: pH,
        working: [
          `${spec.formula} → ${spec.hydroxidePerFormula === 2 ? '2 ' : ''}OH⁻ per formula unit, so [OH⁻] = ${spec.hydroxidePerFormula} × ${formatSci(spec.concentration)} = ${formatSci(hydroxide)} mol L⁻¹.`,
          `pOH = −log₁₀[OH⁻] = ${formatLog(pOH)}`,
          `pH = 14.00 − pOH = ${formatLog(pH)}`,
        ],
        misconceptions: [
          {
            value: pOH,
            feedback:
              'You found pOH. A basic solution has pH above 7, so finish with pH = 14.00 − pOH at 25 °C.',
          },
          ...(spec.hydroxidePerFormula === 2
            ? [
                {
                  value: singleHydroxidePH,
                  feedback: `Each ${spec.formula} releases two OH⁻ ions, so [OH⁻] is twice the base concentration.`,
                },
              ]
            : []),
          {
            value: pFrom(spec.concentration),
            feedback:
              'You treated the base as if it supplied H₃O⁺. For a base, find [OH⁻] and pOH first.',
          },
        ],
        correctReason:
          'A strong base ionises completely, so [OH⁻] comes straight from its formula.',
      });
    }
    case 'h-from-ph': {
      if (!Number.isFinite(spec.pH)) return fail('Enter a pH.');
      const hydronium = 10 ** -spec.pH;
      return solution({
        value: hydronium,
        quantity: 'H',
        modelPH: spec.pH,
        working: [
          `[H₃O⁺] = 10^(−pH) = 10^(−${formatLog(spec.pH)}) = ${formatSci(hydronium)} mol L⁻¹`,
        ],
        misconceptions: [
          {
            value: 10 ** spec.pH,
            feedback:
              'Keep the minus sign: [H₃O⁺] = 10^(−pH). A pH above 0 always gives [H₃O⁺] below 1.',
          },
          {
            value: 10 ** -(PKW - spec.pH),
            feedback: 'That is [OH⁻]. The question asks for [H₃O⁺] = 10^(−pH).',
          },
        ],
        correctReason: 'pH is a logarithm, so undoing it needs a power of ten.',
      });
    }
    case 'oh-from-h': {
      if (!isPositive(spec.hydronium)) return fail('[H₃O⁺] must be above zero.');
      const hydroxide = KW / spec.hydronium;
      return solution({
        value: hydroxide,
        quantity: 'OH',
        modelPH: phFromHydronium(spec.hydronium),
        working: [
          'Kw = [H₃O⁺][OH⁻] = 1.0 × 10⁻¹⁴ at 25 °C',
          `[OH⁻] = Kw ÷ [H₃O⁺] = 1.0 × 10⁻¹⁴ ÷ ${formatSci(spec.hydronium)} = ${formatSci(hydroxide)} mol L⁻¹`,
        ],
        misconceptions: [
          {
            value: KW * spec.hydronium,
            feedback: 'Divide, do not multiply: [OH⁻] = Kw ÷ [H₃O⁺].',
          },
          {
            value: spec.hydronium / KW,
            feedback: 'The division is upside down: [OH⁻] = Kw ÷ [H₃O⁺], not [H₃O⁺] ÷ Kw.',
          },
        ],
        correctReason:
          'In any aqueous solution at 25 °C the two ion concentrations multiply to Kw, so when one rises the other falls.',
      });
    }
    case 'ph-from-oh': {
      if (!isPositive(spec.hydroxide)) return fail('[OH⁻] must be above zero.');
      const pOH = pFrom(spec.hydroxide);
      const pH = PKW - pOH;
      return solution({
        value: pH,
        quantity: 'pH',
        modelPH: pH,
        working: [
          `pOH = −log₁₀(${formatSci(spec.hydroxide)}) = ${formatLog(pOH)}`,
          `pH = 14.00 − ${formatLog(pOH)} = ${formatLog(pH)}`,
        ],
        misconceptions: [
          { value: pOH, feedback: 'That is pOH. Use pH = 14.00 − pOH to finish.' },
          {
            value: -pOH,
            feedback: 'Check the sign: pOH = −log₁₀[OH⁻], then pH = 14.00 − pOH.',
          },
        ],
        correctReason: 'pH + pOH = 14.00 at 25 °C because [H₃O⁺][OH⁻] = 1.0 × 10⁻¹⁴.',
      });
    }
    case 'dilution':
      return solveDilution(spec);
    case 'mixing':
      return solveMixing(spec);
    default:
      return fail('Unsupported strong-solution problem.');
  }
}

function solveDilution(spec: Extract<AcidBasePhSpec, { kind: 'dilution' }>): SolveResult {
  if (![spec.concentration, spec.aliquotMl, spec.finalMl].every(isPositive)) {
    return fail('Concentration and volumes must be above zero.');
  }
  if (spec.finalMl < spec.aliquotMl) return fail('A dilution cannot make the volume smaller.');
  const diluted = (spec.concentration * spec.aliquotMl) / spec.finalMl;
  const ions = diluted * spec.ionsPerFormula;
  if (ions < MIN_STRONG_CONCENTRATION) {
    return fail('Below 1 × 10⁻⁶ mol L⁻¹, water’s own ionisation matters. This model stops.');
  }
  const isAcid = spec.solute === 'acid';
  const pH = isAcid ? phFromHydronium(ions) : phFromHydroxide(ions);
  const undiluted = isAcid
    ? phFromHydronium(spec.concentration * spec.ionsPerFormula)
    : phFromHydroxide(spec.concentration * spec.ionsPerFormula);
  const ion = isAcid ? 'H₃O⁺' : 'OH⁻';
  return solution({
    value: pH,
    quantity: 'pH',
    modelPH: pH,
    working: [
      `c₂ = c₁V₁ ÷ V₂ = ${formatSci(spec.concentration)} × ${spec.aliquotMl} mL ÷ ${spec.finalMl} mL = ${formatSci(diluted)} mol L⁻¹`,
      `[${ion}] = ${formatSci(ions)} mol L⁻¹`,
      isAcid
        ? `pH = −log₁₀(${formatSci(ions)}) = ${formatLog(pH)}`
        : `pOH = ${formatLog(pFrom(ions))}, so pH = 14.00 − ${formatLog(pFrom(ions))} = ${formatLog(pH)}`,
    ],
    misconceptions: [
      {
        value: undiluted,
        feedback: `That is the pH before dilution. Find the new concentration first with c₁V₁ = c₂V₂, then the pH.`,
      },
      ...(isAcid
        ? []
        : [{ value: pFrom(ions), feedback: 'That is pOH. Finish with pH = 14.00 − pOH.' }]),
    ],
    correctReason: `Diluting by a factor of ${formatSci(spec.finalMl / spec.aliquotMl)} lowers [${ion}] by the same factor, so the pH moves towards 7 but never crosses it.`,
  });
}

function solveMixing(spec: Extract<AcidBasePhSpec, { kind: 'mixing' }>): SolveResult {
  const { acid, base } = spec;
  if (![acid.concentration, acid.volumeMl, base.concentration, base.volumeMl].every(isPositive)) {
    return fail('Concentrations and volumes must be above zero.');
  }
  const acidMmol = acid.concentration * acid.volumeMl;
  const hydroxideMmol = base.concentration * base.volumeMl * base.hydroxidePerFormula;
  const totalMl = acid.volumeMl + base.volumeMl;
  const excess = acidMmol - hydroxideMmol;
  const lines = [
    `n(H₃O⁺) = ${formatSci(acid.concentration)} mol L⁻¹ × ${acid.volumeMl} mL = ${formatSci(acidMmol)} mmol`,
    `n(OH⁻) = ${formatSci(base.concentration)} mol L⁻¹ × ${base.volumeMl} mL${base.hydroxidePerFormula === 2 ? ' × 2' : ''} = ${formatSci(hydroxideMmol)} mmol`,
    `H₃O⁺ + OH⁻ → 2H₂O uses them up 1 : 1. Total volume = ${totalMl} mL.`,
  ];
  if (Math.abs(excess) <= FLOAT_EPSILON * Math.max(acidMmol, hydroxideMmol)) {
    return solution({
      value: NEUTRAL_PH,
      quantity: 'pH',
      modelPH: NEUTRAL_PH,
      working: [...lines, 'Neither is in excess, so the solution is neutral: pH = 7.00 at 25 °C.'],
      misconceptions: [
        {
          value: phFromHydronium(acid.concentration),
          feedback:
            'That is the pH of the acid before mixing. Equal amounts of H₃O⁺ and OH⁻ neutralise each other completely.',
        },
      ],
      correctReason:
        'A strong acid and strong base in exactly equal amounts leave only water and a neutral salt.',
    });
  }
  const acidExcess = excess > 0;
  const excessMmol = Math.abs(excess);
  const concentration = excessMmol / totalMl;
  const pH = acidExcess ? phFromHydronium(concentration) : phFromHydroxide(concentration);
  const ion = acidExcess ? 'H₃O⁺' : 'OH⁻';
  const excessVolume = acidExcess ? acid.volumeMl : base.volumeMl;
  const wrongVolumeConcentration = excessMmol / excessVolume;
  const wrongVolumePH = acidExcess
    ? phFromHydronium(wrongVolumeConcentration)
    : phFromHydroxide(wrongVolumeConcentration);
  return solution({
    value: pH,
    quantity: 'pH',
    modelPH: pH,
    working: [
      ...lines,
      `Excess ${ion} = ${formatSci(excessMmol)} mmol in ${totalMl} mL → [${ion}] = ${formatSci(concentration)} mol L⁻¹`,
      acidExcess
        ? `pH = −log₁₀(${formatSci(concentration)}) = ${formatLog(pH)}`
        : `pOH = ${formatLog(pFrom(concentration))}, so pH = 14.00 − ${formatLog(pFrom(concentration))} = ${formatLog(pH)}`,
    ],
    misconceptions: [
      {
        value: wrongVolumePH,
        feedback: `After mixing, the excess ${ion} is spread through the total volume (${totalMl} mL), not only the ${excessVolume} mL it came from.`,
      },
      {
        value: acidExcess
          ? phFromHydronium(acid.concentration)
          : phFromHydroxide(base.concentration * base.hydroxidePerFormula),
        feedback: `Neutralisation happens first. Subtract the ${acidExcess ? 'OH⁻' : 'H₃O⁺'} that reacts before you find the pH.`,
      },
      ...(acidExcess
        ? []
        : [
            { value: pFrom(concentration), feedback: 'That is pOH. Finish with pH = 14.00 − pOH.' },
          ]),
    ],
    correctReason: `${acidExcess ? 'The acid' : 'The base'} is in excess. Only the leftover ${ion}, spread through the whole mixture, sets the pH.`,
  });
}

function solveWeak(spec: AcidBasePhSpec): SolveResult {
  if (!('speciesId' in spec)) return fail('Unsupported weak-solution problem.');
  const species = weakSpecies(spec.speciesId);
  if (!species) return fail('This acid or base is not in the reviewed data.');
  const k = species.k;
  const kLabel = species.type === 'acid' ? 'Ka' : 'Kb';
  switch (spec.kind) {
    case 'weak-acid-ph':
    case 'weak-acid-percent':
    case 'weak-base-ph': {
      const c = spec.concentration;
      if (!isPositive(c)) return fail('Concentration must be above zero.');
      const isBase = species.type === 'base';
      if (isBase !== (spec.kind === 'weak-base-ph')) {
        return fail('This problem type does not match the species.');
      }
      const exact = exactIonised(k, c);
      const approx = approximateIonised(k, c);
      const approximatePercent = (approx / c) * PERCENT;
      const exactPercent = (exact / c) * PERCENT;
      const valid = approximatePercent <= APPROXIMATION_LIMIT_PERCENT;
      const ion = isBase ? 'OH⁻' : 'H₃O⁺';
      const pH = isBase ? phFromHydroxide(exact) : phFromHydronium(exact);
      const approxPH = isBase ? phFromHydroxide(approx) : phFromHydronium(approx);
      const equation = isBase
        ? `${species.formula} + H₂O ⇌ ${species.conjugate} + OH⁻`
        : `${species.formula} + H₂O ⇌ H₃O⁺ + ${species.conjugate}`;
      const working = [
        equation,
        `${kLabel} = x² ÷ (c − x) = ${formatSci(k, 2)}, with c = ${formatSci(c)} mol L⁻¹ and x = [${ion}]`,
        `Shortcut: x ≈ √(${kLabel} × c) = ${formatSci(approx)} mol L⁻¹ → ${formatSci(approximatePercent)} % ionised`,
        valid
          ? `${formatSci(approximatePercent)} % ≤ 5 %, so the shortcut is acceptable. The exact quadratic gives x = ${formatSci(exact)} mol L⁻¹.`
          : `${formatSci(approximatePercent)} % > 5 %, so the shortcut fails. Solve x² + ${kLabel}·x − ${kLabel}·c = 0: x = ${formatSci(exact)} mol L⁻¹.`,
      ];
      const approximation = { approximatePercent, valid };
      const invalidShortcut = valid
        ? []
        : [
            {
              value: spec.kind === 'weak-acid-percent' ? approximatePercent : approxPH,
              feedback: `You used x ≈ √(${kLabel} × c), but that predicts ${formatSci(approximatePercent)} % ionisation, above the 5 % limit. Solve the quadratic instead.`,
            },
          ];
      if (spec.kind === 'weak-acid-percent') {
        return solution({
          value: exactPercent,
          quantity: 'percent',
          modelPH: pH,
          modelPercent: exactPercent,
          approximation,
          working: [
            ...working,
            `% ionisation = x ÷ c × 100 = ${formatSci(exact)} ÷ ${formatSci(c)} × 100 = ${formatSci(exactPercent)} %`,
          ],
          misconceptions: [
            ...invalidShortcut,
            {
              value: exactPercent / PERCENT,
              feedback: 'That is the fraction ionised. Multiply by 100 to give a percentage.',
            },
            {
              value: PERCENT,
              feedback: `${species.name} is a weak acid. Only a small fraction of its molecules donate H⁺ at equilibrium.`,
            },
          ],
          correctReason: `Only about ${formatSci(exactPercent, 2)} % of ${species.formula} ionises; most stays as molecules.`,
        });
      }
      const pOHValue = PKW - pH;
      return solution({
        value: pH,
        quantity: 'pH',
        modelPH: pH,
        modelPercent: exactPercent,
        approximation,
        working: [
          ...working,
          isBase
            ? `pOH = −log₁₀(${formatSci(exact)}) = ${formatLog(pOHValue)}, so pH = 14.00 − ${formatLog(pOHValue)} = ${formatLog(pH)}`
            : `pH = −log₁₀(${formatSci(exact)}) = ${formatLog(pH)}`,
        ],
        misconceptions: [
          ...invalidShortcut,
          {
            value: isBase ? phFromHydroxide(c) : phFromHydronium(c),
            feedback: `You treated ${species.formula} as fully ionised. It is weak, so [${ion}] is much smaller than c and comes from ${kLabel}.`,
          },
          {
            value: isBase ? phFromHydroxide(k * c) : phFromHydronium(k * c),
            feedback: `x² = ${kLabel} × c, so take the square root before the logarithm.`,
          },
          ...(isBase
            ? [{ value: pOHValue, feedback: 'That is pOH. Finish with pH = 14.00 − pOH.' }]
            : []),
        ],
        correctReason: `${species.formula} is weak, so only ${formatSci(exactPercent, 2)} % ionises and the pH is ${isBase ? 'lower' : 'higher'} than a strong ${species.type} of the same concentration.`,
      });
    }
    case 'ka-from-ph': {
      if (species.type !== 'acid') return fail('This problem type needs a weak acid.');
      if (!isPositive(spec.concentration) || !Number.isFinite(spec.pH)) {
        return fail('Concentration and pH are needed.');
      }
      const x = 10 ** -spec.pH;
      if (x >= spec.concentration) {
        return fail('This pH needs more H₃O⁺ than the acid can supply. Check the data.');
      }
      const ka = (x * x) / (spec.concentration - x);
      return solution({
        value: ka,
        quantity: 'Ka',
        modelPH: spec.pH,
        modelPercent: (x / spec.concentration) * PERCENT,
        working: [
          `[H₃O⁺] = [${species.conjugate}] = 10^(−${formatLog(spec.pH)}) = ${formatSci(x)} mol L⁻¹`,
          `[${species.formula}] = ${formatSci(spec.concentration)} − ${formatSci(x)} = ${formatSci(spec.concentration - x)} mol L⁻¹`,
          `Ka = [H₃O⁺][${species.conjugate}] ÷ [${species.formula}] = ${formatSci(ka)}`,
        ],
        misconceptions: [
          {
            value: x / spec.concentration,
            feedback:
              'That is the fraction ionised. Ka multiplies the two product concentrations: x² ÷ (c − x).',
          },
          {
            value: x / (spec.concentration - x),
            feedback:
              'Both H₃O⁺ and the conjugate base are products, so square x: Ka = x² ÷ (c − x).',
          },
        ],
        correctReason: `Each ${species.formula} that ionises gives one H₃O⁺ and one ${species.conjugate}, so both equal 10^(−pH).`,
      });
    }
    case 'pka-from-ka': {
      if (species.type !== 'acid') return fail('This problem type needs a weak acid.');
      const pKa = pFrom(k);
      return solution({
        value: pKa,
        quantity: 'pKa',
        working: [`pKa = −log₁₀ Ka = −log₁₀(${formatSci(k, 2)}) = ${formatLog(pKa)}`],
        misconceptions: [
          { value: -Math.log(k), feedback: 'Use log base 10, not the natural logarithm (ln).' },
          { value: -pKa, feedback: 'Keep the minus sign: pKa = −log₁₀ Ka.' },
        ],
        correctReason: `A smaller Ka gives a larger pKa. ${species.formula} with pKa ${formatLog(pKa)} is a ${pKa > 7 ? 'very weak' : 'weak'} acid.`,
      });
    }
    case 'kb-from-ka': {
      if (species.type !== 'acid') return fail('This problem type needs a weak acid.');
      const kb = KW / k;
      return solution({
        value: kb,
        quantity: 'Kb',
        working: [
          `Ka × Kb = Kw for a conjugate pair at 25 °C`,
          `Kb(${species.conjugate}) = 1.0 × 10⁻¹⁴ ÷ ${formatSci(k, 2)} = ${formatSci(kb)}`,
        ],
        misconceptions: [
          {
            value: k,
            feedback: 'Ka and Kb of a conjugate pair are not equal. Their product is Kw.',
          },
          { value: 1 / k, feedback: 'Divide Kw, not 1, by Ka: Kb = Kw ÷ Ka.' },
          {
            value: PKW - pFrom(k),
            feedback: 'That is pKb (14.00 − pKa). The question asks for Kb itself.',
          },
        ],
        correctReason: `The weaker the acid, the stronger its conjugate base: Ka × Kb = 1.0 × 10⁻¹⁴.`,
      });
    }
    default:
      return fail('Unsupported weak-solution problem.');
  }
}

const WEAK_KINDS: ReadonlySet<AcidBasePhSpec['kind']> = new Set([
  'weak-acid-ph',
  'weak-acid-percent',
  'weak-base-ph',
  'ka-from-ph',
  'pka-from-ka',
  'kb-from-ka',
]);

export function solveAcidBase(spec: AcidBasePhSpec): SolveResult {
  return WEAK_KINDS.has(spec.kind) ? solveWeak(spec) : solveStrong(spec);
}

export function withinAnswerTolerance(
  answer: number,
  expected: number,
  quantity: AcidBaseQuantity,
): boolean {
  if (!Number.isFinite(answer) || !Number.isFinite(expected)) return false;
  if (LOG_QUANTITIES.has(quantity)) return Math.abs(answer - expected) <= LOG_ANSWER_TOLERANCE;
  if (expected === 0) return Math.abs(answer) <= FLOAT_EPSILON;
  return Math.abs(answer - expected) <= Math.abs(expected) * RELATIVE_ANSWER_TOLERANCE;
}

export function formatQuantityValue(value: number, quantity: AcidBaseQuantity): string {
  const text = LOG_QUANTITIES.has(quantity) ? formatLog(value) : formatSci(value);
  const unit = QUANTITY_UNITS[quantity];
  return unit ? `${text} ${unit}` : text;
}

/** Marks a learner answer: accept, then diagnose a known wrong method, then give direction. */
export function checkAcidBaseAnswer(problem: AcidBaseProblem, rawAnswer: string): AcidBaseCheck {
  const solved = solveAcidBase(problem.spec);
  if (!solved.value) {
    return { status: 'invalid', title: 'Problem unavailable', feedback: solved.error };
  }
  const answer = parseNumericAnswer(rawAnswer);
  if (!Number.isFinite(answer)) {
    return {
      status: 'invalid',
      title: 'Enter a number',
      feedback: 'Type a number such as 2.87, 0.0013 or 1.3e-3. Leave the units out.',
    };
  }
  const { value, quantity, misconceptions, correctReason } = solved.value;
  if (withinAnswerTolerance(answer, value, quantity)) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `${correctReason} ${QUANTITY_LABELS[quantity]} = ${formatQuantityValue(value, quantity)}.`,
    };
  }
  const diagnosed = misconceptions.find(
    (item) =>
      Number.isFinite(item.value) &&
      !withinAnswerTolerance(item.value, value, quantity) &&
      withinAnswerTolerance(answer, item.value, quantity),
  );
  if (diagnosed) {
    return {
      status: 'misconception',
      title: 'Not yet — check your method',
      feedback: diagnosed.feedback,
    };
  }
  const direction = answer > value ? 'too large' : 'too small';
  return {
    status: 'incorrect',
    title: 'Not yet',
    feedback: `Your answer is ${direction}. The model gives ${QUANTITY_LABELS[quantity]} = ${formatQuantityValue(value, quantity)}. Compare each step of the working with yours.`,
  };
}
